import { cn } from '@/lib/utils';
import { useDialog } from '@/lib/useDialog';
import { X } from 'lucide-react';
import { ReactNode, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    footer?: ReactNode;
    size?: 'sm' | 'md' | 'lg';
    // Defaults to true; set false to require explicit close (e.g. mid-task).
    closeOnBackdropClick?: boolean;
    className?: string;
}

const SIZE = {
    sm: 'w-[420px]',
    md: 'w-[480px]',
    lg: 'w-[640px]',
} as const;

// Centered modal dialog. Handles Escape, focus trap, focus return,
// scroll-lock, scrim, and labelled header. Use for centered dialogs;
// slide-in drawers (HoldingDetailDrawer, AgentPanel) have their own
// shells but can share useDialog() for the same a11y guarantees.
//
// Rendered through a portal into <body>, never in place. Callers commonly
// mount the dialog right next to its trigger (UploadButton renders the
// <Button> and the drawer as siblings), so an in-place dialog inherits the
// trigger's DOM position — and any sibling that also claims z-50, such as a
// HoverPopover hint panel wrapping the trigger, then paints over it purely on
// tree order. The portal puts the dialog last in <body> so it always wins.
// The dialog sits at z-[70], above the z-[60] review drawers, so a dialog that
// opens from a drawer shows above it. Toasts stay above at z-[9999].
export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    closeOnBackdropClick = true,
    className,
}: ModalProps) {
    const ref = useDialog({ isOpen, onClose });
    const titleId = useId();

    useEffect(() => {
        if (!isOpen) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-[var(--septima-scrim)]"
                onClick={closeOnBackdropClick ? onClose : undefined}
            />
            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className={cn(
                    'relative bg-[rgb(var(--color-surface-raised))] rounded-card shadow-xl flex flex-col max-h-[85vh]',
                    'border border-[rgb(var(--color-divider))]',
                    SIZE[size],
                    'max-w-full',
                    className,
                )}
            >
                <header className="flex h-8 shrink-0 items-center justify-between gap-2 px-4 border-b border-[rgb(var(--color-divider))]">
                    <h2
                        id={titleId}
                        className="truncate text-[13px] font-semibold leading-[18px] text-[rgb(var(--color-text-primary))]"
                    >
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close dialog"
                        className="grid h-6 w-6 shrink-0 place-items-center rounded-button hover:bg-[rgb(var(--color-surface-secondary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent))]"
                    >
                        <X className="w-3.5 h-3.5 text-[rgb(var(--color-text-tertiary))]" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto px-4 py-3">{children}</div>

                {footer && (
                    <footer className="px-4 py-2 border-t border-[rgb(var(--color-divider))] flex items-center justify-end gap-2">
                        {footer}
                    </footer>
                )}
            </div>
        </div>,
        document.body,
    );
}
